import { useState } from 'react'
import {
  MultipleDownloadCreateResponse,
  MultipleDownloadStatusResponse,
  useClient,
} from '@/stores/useClient.ts'
import { DownloadQuality } from '@/models/downloadOptions.ts'
import useDownloadProgress from '@/hooks/useDownloadProgress.ts'

/**
 * Hook encapsulating multi-file download: request creation, polling, and auto-trigger.
 * Reused by FileSelection (B2C) and AlbumCard (portal).
 */
export default function useMultiDownload() {
  const { requestMultiDownload, getMultiDownload } = useClient()
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadRequest, setDownloadRequest] = useState<MultipleDownloadCreateResponse | null>(null)
  const { progress, reportProgress, resetProgress } = useDownloadProgress()

  /**
   * Start a multi-file download. Polls until done, then triggers browser download.
   * Throws on request creation failure (caller should handle).
   * Returns the create response for callers that need metadata (file count, projected size).
   */
  async function startDownload(
    driveId: string,
    albumIds: string[],
    fileIds: string[],
    quality: DownloadQuality,
    onDone?: () => void,
  ): Promise<MultipleDownloadCreateResponse> {
    if (isDownloading) throw new Error('Download already in progress')

    setIsDownloading(true)
    resetProgress()
    let res
    try {
      res = await requestMultiDownload(driveId, albumIds, fileIds, quality)
    } catch (e) {
      setIsDownloading(false)
      throw e
    }
    const createData = res.data
    setDownloadRequest(createData)

    // Poll for completion. `progress` only ever grows (answers can overtake each
    // other) and is never treated as a completion signal on its own.
    const intervalId = setInterval(async () => {
      try {
        const statusRes = await getMultiDownload(driveId, createData.id)
        const data: MultipleDownloadStatusResponse = statusRes.data

        if (data.status !== 'failed') {
          reportProgress(data.progress)
        }

        if (data.status === 'done') {
          clearInterval(intervalId)
          setIsDownloading(false)
          const link = document.createElement('a')
          link.href = data.download_url!
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          onDone?.()
        } else if (data.status === 'failed') {
          clearInterval(intervalId)
          setIsDownloading(false)
          alert('Download fehlgeschlagen')
        }
      } catch {
        clearInterval(intervalId)
        setIsDownloading(false)
        alert('Download fehlgeschlagen')
      }
    }, 1000)

    return createData
  }

  return { startDownload, isDownloading, downloadRequest, progress }
}
